import type { FieldGravityFlow, FieldPoint } from './field';
import type { RaceEventType } from './types';

export interface GravityFlowTarget extends FieldPoint {
  readonly blendSeconds: number;
  readonly inFinalArena: boolean;
}

/** 위치는 사발 판정에, 실제 가이드 진행도는 연결 회랑의 방향 보간에 사용한다. */
export function gravityFlowTarget(
  flow: FieldGravityFlow,
  progress: number,
  x: number,
  y: number,
  eventType?: RaceEventType,
  envelope = 0,
): GravityFlowTarget {
  const stations = flow.stations;
  const inside = stations.findIndex((station) => Math.hypot(x - station.x, y - station.y) <= station.r);
  const inFinalArena = inside === stations.length - 1;
  const blendSeconds = !inFinalArena && eventType === flow.eventType
    ? flow.blendSeconds + (Math.max(.15, flow.eventBlendSeconds) - flow.blendSeconds) * envelope
    : flow.blendSeconds;
  if (inside >= 0) {
    const station = stations[inside];
    return { x: station.gx, y: station.gy, blendSeconds, inFinalArena };
  }
  const next = stations.findIndex((station) => station.progress >= progress);
  if (next <= 0) {
    const station = next === 0 ? stations[0] : stations[stations.length - 1];
    return { x: station.gx, y: station.gy, blendSeconds, inFinalArena };
  }
  const from = stations[next - 1];
  const to = stations[next];
  const length = Math.max(1, Math.hypot(to.x - from.x, to.y - from.y));
  const local = (progress - from.progress) / Math.max(.00001, to.progress - from.progress);
  const entrance = from.r / length;
  const exit = 1 - to.r / length;
  const t = Math.max(0, Math.min(1, (local - entrance) / Math.max(.01, exit - entrance)));
  const eased = t * t * (3 - 2 * t);
  const fromAngle = Math.atan2(from.gy, from.gx);
  const difference = Math.atan2(Math.sin(Math.atan2(to.gy, to.gx) - fromAngle), Math.cos(Math.atan2(to.gy, to.gx) - fromAngle));
  const blendedAngle = fromAngle + difference * eased;
  // 다음 사발의 출구가 뒤쪽(예: B→C→D, H→Z→결승)이면 입구에 닿기도 전에
  // 중력이 회랑을 거슬러 안정점에 갇힌다. 회랑에서는 전진 성분을 최소 31% 남기고,
  // 사발 안에 진입한 뒤 해당 사발의 고정 방향으로 시간 보간한다.
  const corridorAngle = Math.atan2(to.y - from.y, to.x - from.x);
  const offset = Math.atan2(Math.sin(blendedAngle - corridorAngle), Math.cos(blendedAngle - corridorAngle));
  const angle = corridorAngle + Math.max(-Math.PI * .4, Math.min(Math.PI * .4, offset));
  const magnitude = Math.hypot(from.gx, from.gy) * (1 - eased) + Math.hypot(to.gx, to.gy) * eased;
  return { x: Math.cos(angle) * magnitude, y: Math.sin(angle) * magnitude, blendSeconds, inFinalArena };
}
